/**
 * Thin API client — the single place that knows the base URL, JSON headers
 * and how identity travels to the backend.
 *
 * Identity is the session token minted at login (`user.token`), sent as
 * `Authorization: Bearer`. Every protected endpoint verifies it; nothing on
 * the backend trusts a username in a header, query param or body any more.
 *
 * Returns the parsed JSON body (the backend's { success, ... } envelope).
 * Network errors and non-JSON responses throw — callers decide how to react.
 */

import { API_URL } from '../constants/dashboard';
import { getStoredUser, clearStoredUser, mergeStoredUser } from '../auth/userStorage';

/**
 * Headers proving who the caller is.
 * @param {'admin'|'user'} kind — kept for call-site readability; both send the same token
 * @param {object} [user] — session object to read the token from, when it is not
 *   in localStorage yet (the GDPR consent step runs before login completes)
 */
export function authHeaders(kind = 'user', user) {
  const stored = user || getStoredUser();
  return stored?.token ? { Authorization: `Bearer ${stored.token}` } : {};
}

/** Fired on window when a dead session is dropped — App and the store listen. */
export const SESSION_RESET_EVENT = 'lc:session-reset';
const RENEW_HEADER = 'X-Session-Token';

function bearerOf(init) {
  const h = init && init.headers;
  if (!h) return null;
  const v = typeof h.get === 'function' ? h.get('Authorization') : (h.Authorization || h.authorization);
  return typeof v === 'string' && v.startsWith('Bearer ') ? v.slice(7) : null;
}

/** Where a dropped session lands: login, keeping a bot code if the URL carries one. */
export function loginUrlAfterReset(loc = window.location) {
  const params = new URLSearchParams(loc.search);
  const keep = new URLSearchParams({ expired: '1' });
  if (params.get('code') && params.get('user')) {
    keep.set('code', params.get('code'));
    keep.set('user', params.get('user'));
  }
  return `/login?${keep.toString()}`;
}

/**
 * Drop the session everywhere at once: storage, the header's name cache, and —
 * via SESSION_RESET_EVENT — the React state in App and the dashboard store,
 * which used to write the dead user straight back into storage.
 * On /login nothing navigates (the page is where it needs to be, and a pending
 * bot-code verify must not be interrupted); anywhere else → login.
 */
export function resetSession() {
  clearStoredUser();
  try {
    Object.keys(sessionStorage)
      .filter((k) => k.startsWith('header_profile_'))
      .forEach((k) => sessionStorage.removeItem(k));
  } catch (e) { /* storage unavailable — nothing to clear */ }
  window.dispatchEvent(new Event(SESSION_RESET_EVENT));
  if (!window.location.pathname.startsWith('/login')) {
    window.location.replace(loginUrlAfterReset());
  }
}

/**
 * Look at every response to a request that carried our bearer token:
 *  - 401 → the session is dead: reset it. Only if the token that failed is
 *    still the stored one — a 401 for an old token racing a fresh bot login
 *    must not wipe the new session.
 *  - X-Session-Token → the server renewed a token past half-life (sliding
 *    expiration); keep it, again only if it renews the token we still hold.
 */
export function inspectAuthResponse(response, sentToken) {
  if (!response || !sentToken) return;
  const current = getStoredUser()?.token;
  if (current !== sentToken) return;

  if (response.status === 401) {
    resetSession();
    return;
  }
  const renewed = response.headers && typeof response.headers.get === 'function'
    ? response.headers.get(RENEW_HEADER)
    : null;
  if (renewed && renewed !== current) mergeStoredUser({ token: renewed });
}

/**
 * One place that sees every authenticated call. Before 2026-09-24 only some
 * call sites handled 401 — the header and the dashboard bootstrap swallowed it,
 * so a 30-day-old dead session lived on in storage and the login page trusted
 * it (card lc-login-stale-session-fix). Wrapping fetch catches all ~30 raw
 * call sites without touching them. Requests without our bearer pass through.
 */
export function installAuthInterceptor(win = window) {
  if (!win.fetch || win.fetch.__lcAuth) return;
  const original = win.fetch.bind(win);
  const wrapped = async (input, init) => {
    const sentToken = bearerOf(init) || (input && typeof input === 'object' ? bearerOf(input) : null);
    const response = await original(input, init);
    try { inspectAuthResponse(response, sentToken); } catch (e) { /* never break the call */ }
    return response;
  };
  wrapped.__lcAuth = true;
  win.fetch = wrapped;
}

/**
 * Kept for call sites that branch on auth failure. The reset itself happens in
 * the interceptor, which knows which token failed.
 * @returns true if the response was an auth failure.
 */
export function handleAuthFailure(response) {
  return !!response && response.status === 401;
}

export async function apiFetch(path, { method = 'GET', body, headers = {}, auth, user } = {}) {
  const opts = { method, headers: { ...headers } };

  if (body !== undefined) {
    opts.headers['Content-Type'] = 'application/json';
    opts.body = JSON.stringify(body);
  }

  if (auth) {
    Object.assign(opts.headers, authHeaders(auth, user));
  }

  const response = await fetch(`${API_URL}${path}`, opts);
  if (auth) handleAuthFailure(response);
  return response.json();
}

/**
 * Raw fetch for call sites that still build their own URLs (admin tabs).
 * Adds the bearer token and handles 401 — returns the Response, not JSON.
 */
export async function adminFetch(url, options = {}) {
  const response = await fetch(url, {
    ...options,
    headers: { ...authHeaders('admin'), ...(options.headers || {}) },
  });
  handleAuthFailure(response);
  return response;
}

export const api = {
  get: (path, options) => apiFetch(path, { ...options, method: 'GET' }),
  post: (path, body, options) => apiFetch(path, { ...options, method: 'POST', body }),
  put: (path, body, options) => apiFetch(path, { ...options, method: 'PUT', body }),
  patch: (path, body, options) => apiFetch(path, { ...options, method: 'PATCH', body }),
  delete: (path, options) => apiFetch(path, { ...options, method: 'DELETE' }),
};
